import React from 'react';
import { Title, Text, Divider } from '@mantine/core';
import dayjs from 'dayjs';

import { Request } from '../../codegen/graphql';
import formatCurrency from '../../utility/formatCurrency';

type Props = {
    flatRequest: Request | null;
};

function InterimPriceInfo({ flatRequest }: Props) {
    if (!flatRequest) {
        return null;
    }

    const hasTemporaryPrices =
        flatRequest.temporaryCashbuildPrice !== null ||
        flatRequest.temporaryPnlPrice !== null ||
        flatRequest.temporaryChangedPrice !== null;

    if (!hasTemporaryPrices) {
        return null;
    }

    const currency = flatRequest.currentPriceCurrency || 'ZAR';

    const hasCashbuildPrice =
        flatRequest?.temporaryCashbuildPrice !== null &&
        flatRequest?.temporaryCashbuildPrice !== undefined;
    const hasPnlPrice =
        flatRequest?.temporaryPnlPrice !== null &&
        flatRequest?.temporaryPnlPrice !== undefined;
    const hasLegacyPrice =
        flatRequest?.temporaryChangedPrice !== null &&
        flatRequest?.temporaryChangedPrice !== undefined;

    return (
        <div className="flex flex-col gap-4 flex-1 shadow-sm bg-white px-5 py-4 rounded-lg mb-5">
            <div>
                <Title order={4}>Interim Price Change</Title>
                <Text color="dimmed" size="sm">
                    While waiting for supplier engagement.
                </Text>
            </div>

            {hasCashbuildPrice && (
                <div>
                    <Text className="uppercase text-xs text-[#7D8FB3] font-bold mb-2">
                        INTERIM CASHBUILD PRICE
                    </Text>
                    <div className="flex h-full">
                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price
                            </Text>
                            <Text className="font-bold text-primary">
                                {formatCurrency(
                                    flatRequest?.temporaryCashbuildPrice || 0,
                                    currency,
                                )}
                            </Text>
                        </div>

                        <Divider
                            orientation="vertical"
                            color="#e6e9ef"
                            my="xs"
                        />

                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price Valid From
                            </Text>
                            <Text className="font-bold text-primary">
                                {flatRequest.temporaryChangedPriceValidFrom
                                    ? dayjs(
                                          flatRequest.temporaryChangedPriceValidFrom,
                                      ).format('D MMM YYYY')
                                    : '—'}
                            </Text>
                        </div>

                        <Divider
                            orientation="vertical"
                            color="#e6e9ef"
                            my="xs"
                        />

                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price Valid To
                            </Text>
                            <Text className="font-bold text-primary">
                                {flatRequest.temporaryChangedPriceValidTo
                                    ? dayjs(
                                          flatRequest.temporaryChangedPriceValidTo,
                                      ).format('D MMM YYYY')
                                    : '—'}
                            </Text>
                        </div>
                    </div>
                </div>
            )}

            {hasPnlPrice && (
                <div>
                    <Text className="uppercase text-xs text-[#7D8FB3] font-bold mb-2">
                        INTERIM P&L PRICE
                    </Text>
                    <div className="flex h-full">
                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price
                            </Text>
                            <Text className="font-bold text-primary">
                                {formatCurrency(
                                    flatRequest?.temporaryPnlPrice || 0,
                                    currency,
                                )}
                            </Text>
                        </div>

                        <Divider
                            orientation="vertical"
                            color="#e6e9ef"
                            my="xs"
                        />

                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price Valid From
                            </Text>
                            <Text className="font-bold text-primary">
                                {flatRequest.temporaryChangedPriceValidFrom
                                    ? dayjs(
                                          flatRequest.temporaryChangedPriceValidFrom,
                                      ).format('D MMM YYYY')
                                    : '—'}
                            </Text>
                        </div>

                        <Divider
                            orientation="vertical"
                            color="#e6e9ef"
                            my="xs"
                        />

                        <div className="flex-1 p-4">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                Interim Price Valid To
                            </Text>
                            <Text className="font-bold text-primary">
                                {flatRequest.temporaryChangedPriceValidTo
                                    ? dayjs(
                                          flatRequest.temporaryChangedPriceValidTo,
                                      ).format('D MMM YYYY')
                                    : '—'}
                            </Text>
                        </div>
                    </div>
                </div>
            )}

            {!hasCashbuildPrice && !hasPnlPrice && hasLegacyPrice && (
                <div className="flex h-full">
                    <div className="flex-1 p-4">
                        <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                            Interim Price
                        </Text>
                        <Text className="font-bold text-primary">
                            {formatCurrency(
                                flatRequest?.temporaryChangedPrice || 0,
                                currency,
                            )}
                        </Text>
                    </div>

                    <Divider orientation="vertical" color="#e6e9ef" my="xs" />

                    <div className="flex-1 p-4">
                        <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                            Interim Price Valid From
                        </Text>
                        <Text className="font-bold text-primary">
                            {flatRequest?.temporaryChangedPriceValidFrom
                                ? dayjs(
                                      flatRequest.temporaryChangedPriceValidFrom,
                                  ).format('D MMM YYYY')
                                : '—'}
                        </Text>
                    </div>

                    <Divider orientation="vertical" color="#e6e9ef" my="xs" />

                    <div className="flex-1 p-4">
                        <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                            Interim Price Valid To
                        </Text>
                        <Text className="font-bold text-primary">
                            {flatRequest?.temporaryChangedPriceValidTo
                                ? dayjs(
                                      flatRequest.temporaryChangedPriceValidTo,
                                  ).format('D MMM YYYY')
                                : '—'}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InterimPriceInfo;
